import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
  Button,
  CircularProgress,
  CssBaseline,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { toast } from "react-toastify";

import { AuthContext } from "../../context/Auth/AuthContext";
import { parseLoginSlides } from "../../components/AuthLayout";
import { applyResellerBranding, resolveBrandingUrl } from "../../utils/branding";
import toastError from "../../errors/toastError";
import api from "../../services/api";

const fallbackSlides = [
  {
    imageUrl:
      "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1400&q=80",
    title: "Atencion organizada",
    description: "Conserva cada conversacion, responsable y seguimiento en contexto.",
  },
  {
    imageUrl:
      "https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&w=1400&q=80",
    title: "Equipos conectados",
    description: "Administra conversaciones, areas y automatizaciones sin perder visibilidad.",
  },
  {
    imageUrl:
      "https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=1400&q=80",
    title: "Seguimiento claro",
    description: "Haz que cada oportunidad avance con recordatorios y secuencias.",
  },
];

const normalizeLinks = links =>
  Array.isArray(links)
    ? links
        .map(link => ({
          label: String(link?.label || "").trim(),
          url: String(link?.url || "").trim(),
        }))
        .filter(link => link.label && link.url)
    : [];

const isExternalUrl = url => /^https?:\/\//i.test(String(url || ""));

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: "100vh",
    display: "grid",
    gridTemplateColumns: "minmax(380px, 47%) minmax(0, 53%)",
    backgroundColor: "#f6f7fb",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      gridTemplateRows: "auto 1fr",
    },
  },
  formSide: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4),
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.97) 0%, rgba(248,250,252,0.98) 100%)",
    [theme.breakpoints.down("sm")]: {
      minHeight: "auto",
      padding: theme.spacing(3, 2, 5),
    },
  },
  shell: {
    width: "100%",
    maxWidth: 480,
  },
  brand: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(4),
  },
  logo: {
    width: 50,
    height: 50,
    borderRadius: 8,
    objectFit: "cover",
  },
  logoFallback: {
    width: 50,
    height: 50,
    borderRadius: 8,
    display: "grid",
    placeItems: "center",
    color: "#fff",
    fontWeight: 800,
    fontSize: 20,
  },
  eyebrow: {
    fontWeight: 700,
    textTransform: "uppercase",
    fontSize: 12,
    letterSpacing: 0,
  },
  title: {
    fontWeight: 800,
    lineHeight: 1.08,
    color: "#111827",
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      fontSize: 30,
      lineHeight: 1.16,
    },
  },
  subtitle: {
    color: "#64748b",
    marginBottom: theme.spacing(2.5),
    lineHeight: 1.5,
  },
  loginForm: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  submit: {
    minHeight: 42,
    fontWeight: 700,
    marginTop: theme.spacing(0.5),
  },
  links: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  plans: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  plan: {
    padding: theme.spacing(1.5),
    borderRadius: 8,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.75),
    border: "1px solid rgba(15,23,42,0.08)",
  },
  price: {
    fontWeight: 800,
  },
  visualSide: {
    position: "relative",
    minHeight: "100vh",
    overflow: "hidden",
    backgroundColor: "#111827",
    [theme.breakpoints.down("sm")]: {
      order: -1,
      minHeight: 230,
    },
  },
  slideImage: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transform: "scale(1.02)",
    transition: "opacity 420ms ease",
  },
  visualOverlay: {
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(180deg, rgba(3,7,18,0.16) 0%, rgba(3,7,18,0.72) 100%)",
  },
  slideContent: {
    position: "absolute",
    left: theme.spacing(6),
    right: theme.spacing(6),
    bottom: theme.spacing(7),
    color: "#fff",
    maxWidth: 620,
    [theme.breakpoints.down("sm")]: {
      left: theme.spacing(3),
      right: theme.spacing(3),
      bottom: theme.spacing(2.5),
    },
  },
  slideTitle: {
    fontWeight: 800,
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      fontSize: 25,
      lineHeight: 1.12,
    },
  },
  slideDescription: {
    color: "rgba(255,255,255,0.84)",
    maxWidth: 540,
    [theme.breakpoints.down("sm")]: {
      fontSize: 15,
      lineHeight: 1.35,
    },
  },
  dots: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      marginTop: theme.spacing(1.5),
    },
  },
  dot: {
    width: 28,
    height: 4,
    border: 0,
    borderRadius: 999,
    background: "rgba(255,255,255,0.35)",
    cursor: "pointer",
    padding: 0,
  },
  dotActive: {
    background: "#fff",
  },
  loader: {
    minHeight: "100vh",
    display: "grid",
    placeItems: "center",
    backgroundColor: "#f6f7fb",
  },
  page: {
    minHeight: "100vh",
    background: "#f6f7fb",
    color: "#111827",
  },
  compactShell: {
    minHeight: "100vh",
    display: "grid",
    gridTemplateColumns: "minmax(280px, 0.95fr) minmax(420px, 1.35fr) minmax(300px, 0.9fr)",
    gap: theme.spacing(2),
    padding: theme.spacing(3),
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr",
    },
  },
  serviceShell: {
    minHeight: "100vh",
    display: "grid",
    gridTemplateColumns: "minmax(420px, 1.15fr) minmax(360px, 0.85fr)",
    gap: theme.spacing(2),
    padding: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      padding: theme.spacing(2),
    },
  },
  serviceMain: {
    display: "grid",
    gridTemplateRows: "52vh auto",
    gap: theme.spacing(2),
    minHeight: 0,
  },
  comparisonShell: {
    minHeight: "100vh",
    padding: theme.spacing(3),
    display: "grid",
    gridTemplateRows: "auto 1fr",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },
  },
  portalShell: {
    minHeight: "100vh",
    display: "grid",
    gridTemplateRows: "minmax(230px, 34vh) 1fr",
  },
  panelCard: {
    borderRadius: 8,
    border: "1px solid rgba(15,23,42,0.08)",
    padding: theme.spacing(2),
    background: "#fff",
    overflow: "hidden",
  },
  tallPanel: {
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  },
  miniHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
    marginBottom: theme.spacing(2),
  },
  headlineBlock: {
    display: "grid",
    gap: theme.spacing(1),
  },
  benefitList: {
    display: "grid",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  benefitItem: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "flex-start",
    color: "#475569",
  },
  benefitMark: {
    width: 8,
    height: 8,
    borderRadius: 99,
    marginTop: 7,
    flex: "0 0 auto",
  },
  loginCard: {
    display: "grid",
    gap: theme.spacing(0.75),
    alignSelf: "start",
  },
  denseLoginForm: {
    display: "grid",
    gap: theme.spacing(1),
  },
  planGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: theme.spacing(1),
    minHeight: 0,
  },
  planCard: {
    borderRadius: 8,
    border: "1px solid rgba(15,23,42,0.08)",
    padding: theme.spacing(1.5),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.75),
    minHeight: 150,
  },
  planCardFeatured: {
    borderWidth: 2,
  },
  planActions: {
    marginTop: "auto",
    alignSelf: "flex-start",
    minWidth: 132,
    minHeight: 34,
  },
  linkStack: {
    display: "grid",
    gap: theme.spacing(1),
  },
  linkPill: {
    justifyContent: "flex-start",
    textTransform: "none",
  },
  serviceMedia: {
    position: "relative",
    minHeight: 0,
    borderRadius: 8,
    overflow: "hidden",
    background: "#111827",
  },
  serviceOverlay: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(180deg, rgba(15,23,42,0.08) 0%, rgba(15,23,42,0.78) 100%)",
  },
  serviceContent: {
    position: "absolute",
    left: theme.spacing(3),
    right: theme.spacing(3),
    bottom: theme.spacing(3),
    color: "#fff",
  },
  serviceSlideText: {
    marginTop: theme.spacing(2),
    maxWidth: 620,
  },
  serviceSide: {
    display: "grid",
    gap: theme.spacing(2),
    alignContent: "start",
  },
  stepsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  stepItem: {
    border: "1px solid rgba(15,23,42,0.08)",
    borderRadius: 8,
    padding: theme.spacing(1.25),
    background: "#f8fafc",
    display: "grid",
    gridTemplateColumns: "auto 1fr",
    gap: theme.spacing(1),
    alignItems: "flex-start",
  },
  stepMarker: {
    width: 24,
    height: 24,
    borderRadius: 99,
    display: "grid",
    placeItems: "center",
    color: "#fff",
    fontSize: 12,
    fontWeight: 700,
  },
  pointMarker: {
    width: 8,
    height: 8,
    marginTop: 8,
  },
  mobileTabs: {
    margin: theme.spacing(2, 2, 0),
    background: "#fff",
    borderRadius: 8,
    border: "1px solid rgba(15,23,42,0.08)",
  },
  mobilePane: {
    padding: theme.spacing(2),
    display: "grid",
    gap: theme.spacing(2),
  },
  comparisonTop: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(300px, 380px)",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  comparisonPlans: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: theme.spacing(1.5),
  },
  portalHero: {
    position: "relative",
    overflow: "hidden",
    color: "#fff",
    background: "#111827",
  },
  portalContent: {
    position: "relative",
    zIndex: 1,
    height: "100%",
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(3),
  },
  portalBody: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(320px, 420px)",
    gap: theme.spacing(2),
    padding: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      padding: theme.spacing(2),
    },
  },
}));

const ResellerSlideShow = React.memo(
  ({
    classes,
    slides,
    brandName,
    resellerName,
    logoUrl,
    primaryColor,
    headline,
    subheadline,
    compact = false,
  }) => {
    const [activeSlide, setActiveSlide] = useState(0);
    const currentSlide = slides[activeSlide] || slides[0];

    useEffect(() => {
      if (slides.length <= 1) return undefined;

      const timer = setInterval(() => {
        setActiveSlide(current => (current + 1) % slides.length);
      }, 5500);

      return () => clearInterval(timer);
    }, [slides.length]);

    useEffect(() => {
      if (activeSlide > slides.length - 1) setActiveSlide(0);
    }, [activeSlide, slides.length]);

    return (
      <>
        {slides.map((slide, index) => (
          <img
            key={`${slide.imageUrl}-${index}`}
            src={slide.imageUrl}
            alt={slide.title || brandName}
            className={classes.slideImage}
            style={{ opacity: index === activeSlide ? 1 : 0 }}
          />
        ))}
        <div className={compact ? classes.serviceOverlay : classes.visualOverlay} />
        <div className={classes.serviceContent}>
          <div className={classes.headlineBlock}>
            <div className={classes.miniHeader}>
              {logoUrl ? (
                <img className={classes.logo} src={logoUrl} alt={brandName} />
              ) : (
                <div className={classes.logoFallback} style={{ background: primaryColor }}>
                  {brandName.slice(0, 1).toUpperCase()}
                </div>
              )}
              <div>
                <Typography
                  className={classes.eyebrow}
                  style={{ color: "rgba(255,255,255,0.72)" }}
                >
                  {brandName}
                </Typography>
                <Typography variant="h6" style={{ color: "#fff" }}>
                  {resellerName}
                </Typography>
              </div>
            </div>
            <Typography component="h1" variant="h4" className={classes.title} style={{ color: "#fff" }}>
              {headline || brandName}
            </Typography>
            <Typography variant="body1" className={classes.subtitle} style={{ color: "rgba(255,255,255,0.82)" }}>
              {subheadline ||
                "Elige tu plan, completa el pago con el enlace indicado y tu proveedor activara la cuenta manualmente."}
            </Typography>
          </div>
          <div className={classes.serviceSlideText}>
            <Typography variant="h4" component="h2" className={classes.slideTitle}>
              {currentSlide.title}
            </Typography>
            <Typography variant="body1" className={classes.slideDescription}>
              {currentSlide.description}
            </Typography>
            <div className={classes.dots}>
              {slides.map((slide, index) => (
                <button
                  key={`${slide.imageUrl}-${index}`}
                  type="button"
                  aria-label={`Slide ${index + 1}`}
                  className={`${classes.dot} ${index === activeSlide ? classes.dotActive : ""}`}
                  onClick={() => setActiveSlide(index)}
                />
              ))}
            </div>
          </div>
        </div>
      </>
    );
  }
);

const ResellerLanding = ({ initialReseller = null }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { slug } = useParams();
  const { handleLogin } = useContext(AuthContext);
  const [reseller, setReseller] = useState(initialReseller);
  const [loading, setLoading] = useState(!initialReseller);
  const [login, setLogin] = useState({ email: "", password: "" });
  const [mobileTab, setMobileTab] = useState("content");
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (initialReseller) {
      setReseller(initialReseller);
      setLoading(false);
      return undefined;
    }

    let mounted = true;

    const loadLanding = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/resellers/public", {
          params: slug ? { slug } : { domain: window.location.hostname },
        });
        if (mounted) setReseller(data);
      } catch (err) {
        toastError(err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadLanding();

    return () => {
      mounted = false;
    };
  }, [initialReseller, slug]);

  const slides = useMemo(() => {
    if (reseller?.loginSlides?.length) {
      return parseLoginSlides(JSON.stringify(reseller.loginSlides));
    }

    return fallbackSlides;
  }, [reseller]);
  const links = useMemo(() => normalizeLinks(reseller?.loginLinks), [reseller]);

  useEffect(() => {
    if (!reseller) return;

    applyResellerBranding({
      title: reseller.brandName || reseller.name,
      logoUrl: reseller.logoUrl || "",
      faviconUrl: reseller.logoUrl || "",
    });
  }, [reseller]);

  const handleSubmit = async event => {
    event.preventDefault();
    try {
      await handleLogin({
        ...login,
        resellerCompanyId: reseller?.id,
        resellerSlug: reseller?.slug,
        resellerDomain: window.location.hostname,
      });
    } catch (err) {
      toastError(err);
    }
  };

  if (loading) {
    return (
      <div className={classes.loader}>
        <CircularProgress />
      </div>
    );
  }

  if (!reseller) {
    return (
      <div className={classes.loader}>
        <Typography variant="h5">Landing no disponible.</Typography>
      </div>
    );
  }

  const primaryColor = reseller.primaryColor || "#1976d2";
  const accentColor = reseller.accentColor || "#10b981";
  const brandName = reseller.brandName || reseller.name;
  const logoUrl = resolveBrandingUrl(reseller.logoUrl);
  const heroHeight = Math.min(Math.max(Number(reseller.landingHeroHeight || 52), 32), 78);
  const plans = reseller.plans || [];
  const defaultLandingSections = [
    {
      title: "Como funciona",
      description: "Compra tu plan con el enlace disponible y el proveedor activara tu cuenta manualmente.",
      markerStyle: "numbers",
      items: [
        {
          title: "Elige",
          description: "Selecciona el plan que encaja con tu equipo.",
        },
        {
          title: "Paga",
          description: "Usa el link configurado por tu proveedor.",
        },
        {
          title: "Entra",
          description: "Recibe tus accesos e inicia sesion aqui mismo.",
        },
      ],
    },
  ];
  const landingSections = reseller.landingSections?.length
    ? reseller.landingSections
    : defaultLandingSections;
  const renderLoginPanel = (title = "Acceso clientes") => (
    <Paper className={`${classes.panelCard} ${classes.loginCard}`} elevation={0}>
      <Typography variant="h6">{title}</Typography>
      <form className={classes.denseLoginForm} onSubmit={handleSubmit}>
        <TextField
          fullWidth
          required
          type="email"
          variant="outlined"
          margin="dense"
          label="Correo"
          value={login.email}
          onChange={event => setLogin(current => ({ ...current, email: event.target.value }))}
        />
        <TextField
          fullWidth
          required
          type="password"
          variant="outlined"
          margin="dense"
          label="Password"
          value={login.password}
          onChange={event => setLogin(current => ({ ...current, password: event.target.value }))}
        />
        <Button
          fullWidth
          color="primary"
          variant="contained"
          type="submit"
          className={classes.submit}
          style={{ backgroundColor: primaryColor }}
        >
          Entrar
        </Button>
      </form>
    </Paper>
  );

  const LinksPanel = () =>
    links.length ? (
      <Paper className={classes.panelCard} elevation={0}>
        <Typography variant="subtitle1">Links utiles</Typography>
        <div className={classes.linkStack}>
          {links.map(link => {
            const external = isExternalUrl(link.url);
            return (
              <Button
                key={`${link.label}-${link.url}`}
                className={classes.linkPill}
                color="primary"
                variant="outlined"
                component={external ? "a" : RouterLink}
                href={external ? link.url : undefined}
                to={external ? undefined : link.url}
                target={external ? "_blank" : undefined}
                rel={external ? "noopener noreferrer" : undefined}
              >
                {link.label}
              </Button>
            );
          })}
        </div>
      </Paper>
    ) : null;

  const PlanCard = ({ plan }) => (
    <Paper
      className={`${classes.planCard} ${plan.highlighted ? classes.planCardFeatured : ""}`}
      elevation={0}
      style={plan.highlighted ? { borderColor: primaryColor } : undefined}
    >
      <Typography variant="subtitle1">{plan.name}</Typography>
      <Typography className={classes.price} style={{ color: primaryColor }}>
        {plan.priceLabel || "Consultar"}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        {plan.description || "Activacion manual por el proveedor."}
      </Typography>
      <Button
        className={classes.planActions}
        size="small"
        color="primary"
        variant="contained"
        href={plan.paymentUrl || undefined}
        target={plan.paymentUrl ? "_blank" : undefined}
        rel={plan.paymentUrl ? "noopener noreferrer" : undefined}
        onClick={() => {
          if (!plan.paymentUrl) toast.info("Solicita el enlace de pago al proveedor.");
        }}
        style={{ backgroundColor: accentColor }}
      >
        {plan.buttonLabel || "Registrarme"}
      </Button>
    </Paper>
  );

  const PlansPanel = () => (
    <div className={classes.planGrid}>
      {plans.map((plan, index) => (
        <PlanCard key={plan.id || index} plan={plan} />
      ))}
    </div>
  );

  const HowItWorksPanel = () => (
    <>
      {landingSections.map((section, sectionIndex) => (
        <Paper className={classes.panelCard} elevation={0} key={`${section.title}-${sectionIndex}`}>
          {section.title && <Typography variant="h6">{section.title}</Typography>}
          {section.description && (
            <Typography variant="body2" color="textSecondary">
              {section.description}
            </Typography>
          )}
          <div className={classes.stepsGrid}>
            {(section.items || []).map((item, itemIndex) => (
              <div className={classes.stepItem} key={`${item.title || item}-${itemIndex}`}>
                <span
                  className={`${classes.stepMarker} ${
                    section.markerStyle === "points" ? classes.pointMarker : ""
                  }`}
                  style={{ background: primaryColor }}
                >
                  {section.markerStyle === "points" ? "" : itemIndex + 1}
                </span>
                <div>
                  <Typography variant="subtitle2">
                    {typeof item === "string" ? item : item.title}
                  </Typography>
                  {typeof item !== "string" && item.description && (
                    <Typography variant="body2" color="textSecondary">
                      {item.description}
                    </Typography>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Paper>
      ))}
    </>
  );

  const renderServiceProfile = () => (
    <main className={classes.page}>
      <CssBaseline />
      {isMobile ? (
        <>
          <section className={classes.mobilePane}>
            <div
              className={classes.serviceMedia}
              style={{ height: `${heroHeight}vh` }}
            >
              <ResellerSlideShow
                classes={classes}
                slides={slides}
                brandName={brandName}
                resellerName={reseller.name}
                logoUrl={logoUrl}
                primaryColor={primaryColor}
                headline={reseller.headline}
                subheadline={reseller.subheadline}
                compact
              />
            </div>
          </section>

          <Tabs
            value={mobileTab}
            onChange={(event, value) => setMobileTab(value)}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            className={classes.mobileTabs}
          >
            <Tab value="content" label={brandName} />
            <Tab value="access" label="Acceso" />
          </Tabs>

          {mobileTab === "content" && (
            <section className={classes.mobilePane}>
              <HowItWorksPanel />
              <Paper className={classes.panelCard} elevation={0}>
                <Typography variant="h6" gutterBottom>Elige un plan</Typography>
                <PlansPanel />
              </Paper>
              <LinksPanel />
            </section>
          )}

          {mobileTab === "access" && (
            <section className={classes.mobilePane}>
              {renderLoginPanel()}
            </section>
          )}
        </>
      ) : (
        <div className={classes.serviceShell}>
          <section className={classes.serviceMain}>
            <div className={classes.serviceMedia}>
              <ResellerSlideShow
                classes={classes}
                slides={slides}
                brandName={brandName}
                resellerName={reseller.name}
                logoUrl={logoUrl}
                primaryColor={primaryColor}
                headline={reseller.headline}
                subheadline={reseller.subheadline}
                compact
              />
            </div>
            <HowItWorksPanel />
          </section>
          <aside className={classes.serviceSide}>
            <Paper className={classes.panelCard} elevation={0}>
              <Typography variant="h6" gutterBottom>Elige un plan</Typography>
              <PlansPanel />
            </Paper>
            {renderLoginPanel()}
            <LinksPanel />
          </aside>
        </div>
      )}
    </main>
  );

  return renderServiceProfile();
};

export default ResellerLanding;
